/**
 * `/demo` — the public demo entry (#1753, #2824).
 *
 * Why the visitor comes: to see, in a few minutes, how Tarii rosters a team
 * like theirs. First they see a short list of industries.
 *
 * Two levels: this page lists industries, and an industry card opens
 * `/demo/industry/:industry` with that industry's teams. The persona picker
 * (`DemoPage`) stays at `/demo/scenarios` for sales demos; this page does
 * not link to it.
 */
import { ArrowLeft } from "lucide-react";
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";

import { DemoEndedNotice } from "../components/DemoEndedNotice";
import { Logo } from "../components/Logo";
import { leaveDemoOnClick } from "../lib/demoSession";
import { DemoIndustryArt } from "./demo/DemoChapterArt";
import { visibleIndustries } from "./demo/demoIndustries";
import { useDemoScenarios } from "./demo/demoScenarios";
import { TariiWord } from "./marketing/TariiWord";
import "./marketing/marketing-brand.css";

export function DemoStartPage() {
  const scenarios = useDemoScenarios();
  const entries = visibleIndustries(scenarios.data ?? []);

  return (
    <div
      data-marketing-brand=""
      className="dc-page-wash flex min-h-screen flex-col"
      data-testid="demo-start-page"
    >
      {/* #2779 — a way back to the website from the demo entry. */}
      <nav className="mx-auto w-full max-w-5xl px-4 pt-6 sm:px-6">
        <a
          href="/"
          onClick={leaveDemoOnClick}
          className="inline-flex min-h-tap items-center gap-1.5 text-sm underline-offset-4 hover:underline"
          data-testid="demo-back-to-site"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to the website
        </a>
      </nav>
      <DemoEndedNotice />
      <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col items-center gap-10 px-4 py-10 sm:px-6 sm:py-14">
        <div className="flex flex-col items-center gap-8">
          <a href="/" onClick={leaveDemoOnClick} aria-label="Back to the Tarii home page">
            <Logo className="h-10" />
          </a>
          <header className="dc-head flex flex-col items-center gap-4 text-center">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-fg-subtle">
              Product demo
            </p>
            <h1>
              Try <TariiWord /> on a team like yours.
            </h1>
          </header>
        </div>

        {scenarios.isPending ? (
          <div className="min-h-[14rem]" aria-hidden="true" data-testid="demo-start-loading" />
        ) : null}

        {scenarios.isError ? (
          <p className="text-sm" role="alert" data-testid="demo-start-error">
            We could not load the demo teams. Try again in a moment.
          </p>
        ) : null}

        {entries.length > 0 ? (
          <ul
            className="grid w-full list-none grid-cols-1 gap-5 p-0 md:grid-cols-2 lg:grid-cols-3"
            data-testid="demo-industry-cards"
          >
            {entries.map(({ industry, teams }, index) => (
              <li key={industry.id} className="dc-card">
                <Link
                  to={`/demo/industry/${industry.id}`}
                  className="flex h-full flex-col no-underline outline-offset-[-3px]"
                  data-testid={`demo-industry-${industry.id}`}
                >
                  <DemoIndustryArt industryId={industry.id} fallback={index % 3} />
                  <div className="flex flex-col gap-1 px-5 pb-5 pt-4">
                    <h2>{industry.name}</h2>
                    <p className="dc-meta">
                      {teams.length === 1 ? "1 team" : `${teams.length} teams`}
                    </p>
                  </div>
                  <div className="dc-more" data-testid={`demo-industry-more-${industry.id}`}>
                    <p className="dc-step" style={{ "--i": 0 } as CSSProperties}>
                      {industry.tagline}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </main>
    </div>
  );
}
